import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import '../styles/ApiAccess.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;

const TIERS = [
  {
    id: 'ngo',
    name: 'NGO / Academic',
    price: 9.99,
    rateLimit: '5,000 req/day',
    badge: 'Research',
    color: '#16a34a',
    features: [
      'Topic listings & search',
      'Sentiment timeseries',
      'Geographic heatmaps',
      'Diaspora breakdowns',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 49.99,
    rateLimit: '50,000 req/day',
    badge: 'Most popular',
    color: '#4f46e5',
    features: [
      'Everything in NGO',
      'AI synthesis & viewpoint clusters',
      'Webhook push events',
      'Perspective analysis',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 299.99,
    rateLimit: 'Unlimited',
    badge: 'Full access',
    color: '#b45309',
    features: [
      'Everything in Professional',
      'Unlimited request rate',
      'Priority support',
      'Custom integrations',
    ],
  },
];


function KeyReveal({ apiKey, tier }) {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  const copyKey = () => {
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="key-reveal">
      <div className="key-reveal__icon">🔑</div>
      <h2 className="key-reveal__title">Your API Key</h2>
      <p className="key-reveal__warning">
        Save this key now — it will <strong>not</strong> be shown again.
      </p>
      <div className="key-reveal__box">
        <code ref={inputRef} className="key-reveal__code">{apiKey}</code>
        <button className="key-reveal__copy" onClick={copyKey}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div className="key-reveal__details">
        <span className="key-reveal__tier">{tier.charAt(0).toUpperCase() + tier.slice(1)} tier</span>
        <span className="key-reveal__limit">
          {TIERS.find(t => t.id === tier)?.rateLimit || ''}
        </span>
      </div>
      <div className="key-reveal__usage">
        <p>Use your key in the <code>X-API-Key</code> header:</p>
        <pre className="key-reveal__example">{`curl https://proteste.net/api/v1/trends \\
  -H "X-API-Key: ${apiKey.substring(0, 24)}..."`}</pre>
        <a
          className="key-reveal__docs"
          href="https://proteste.net/api/docs"
          target="_blank"
          rel="noopener noreferrer"
        >
          View API documentation →
        </a>
      </div>
    </div>
  );
}

function StripePaymentForm({ tier, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);

    try {
      const { data } = await axios.post(`${API_BASE}/keys/purchase/stripe`, {
        tier,
        currency: 'USD',
      });

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        data.client_secret,
        { payment_method: { card: elements.getElement(CardElement) } }
      );

      if (stripeError) {
        setError(stripeError.message);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        const { data: confirmData } = await axios.post(
          `${API_BASE}/keys/purchase/stripe/confirm`,
          { payment_intent_id: paymentIntent.id }
        );
        onSuccess(confirmData.api_key, confirmData.tier);
      } else {
        setError('Payment incomplete. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Payment failed.');
    } finally {
      setLoading(false);
    }
  };

  const tierInfo = TIERS.find(t => t.id === tier);

  return (
    <form className="payment-form" onSubmit={handleSubmit}>
      <div className="payment-form__row">
        <label>Card details</label>
        <div className="card-element-wrapper">
          <CardElement options={{ style: { base: { fontSize: '15px', color: 'inherit' } } }} />
        </div>
      </div>
      {error && <p className="payment-form__error">{error}</p>}
      <button type="submit" disabled={!stripe || loading} className="payment-form__submit">
        {loading ? 'Processing…' : `Pay $${tierInfo?.price.toFixed(2)} USD`}
      </button>
      <p className="payment-form__note">
        Charged once. No subscription. Anonymous — no account required.
      </p>
    </form>
  );
}

function MobileMoneyForm({ tier, onSuccess }) {
  const [phone, setPhone] = useState('');
  const [currency, setCurrency] = useState('');
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  const pollRef = useRef(null);

  // Live from PawaPay instead of a fixed, hand-maintained list — this is
  // what previously had no DRC/CDF at all, and never offered USD anywhere
  // even where PawaPay actually supports it alongside the local currency.
  const [countries, setCountries] = useState([]);
  const [countriesError, setCountriesError] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE}/pawapay/providers`)
      .then(({ data }) => setCountries(data.countries || []))
      .catch(() => setCountriesError(true));
  }, []);

  // Currencies available for the selected country, deduplicated across all
  // of that country's providers — the specific provider is auto-detected
  // server-side from the phone number, so the payer only needs to pick a
  // currency they actually hold funds in.
  const currencyOptions = (() => {
    const country = countries.find(c => c.country === selectedCountry);
    if (!country) return [];
    const seen = new Map();
    for (const provider of country.providers || []) {
      for (const curr of provider.currencies || []) {
        if (curr.operationTypes?.DEPOSIT?.status !== 'OPERATIONAL') continue;
        if (!seen.has(curr.currency)) {
          seen.set(curr.currency, curr.displayName || curr.currency);
        }
      }
    }
    return Array.from(seen, ([code, label]) => ({ code, label }));
  })();

  const handleCountryChange = (countryCode) => {
    setSelectedCountry(countryCode);
    setCurrency('');
  };

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
  };

  useEffect(() => () => stopPolling(), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data } = await axios.post(`${API_BASE}/keys/purchase/mobile-money`, {
        tier,
        phone_number: phone,
        currency,
      });
      setPolling(true);
      setStatusMsg('Approve the payment on your phone. Checking every 5 seconds…');
      startPolling(data.deposit_id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to initiate payment.');
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/keys/purchase/${id}/status`);
        if (data.status === 'fulfilled') {
          stopPolling();
          setPolling(false);
          onSuccess(data.api_key, data.tier);
        } else if (data.status === 'failed') {
          stopPolling();
          setPolling(false);
          setError('Payment failed or was rejected. Please try again.');
        } else {
          setStatusMsg(data.message || 'Waiting for payment confirmation…');
        }
      } catch {
        // network hiccup — keep polling
      }
    }, 5000);
  };

  const tierInfo = TIERS.find(t => t.id === tier);

  return (
    <div>
      {!polling && (
        <form className="payment-form" onSubmit={handleSubmit}>
          {countriesError && (
            <p className="payment-form__error">
              Could not load mobile money providers. Please try again shortly.
            </p>
          )}
          <div className="payment-form__row">
            <label>Country</label>
            <select
              value={selectedCountry}
              onChange={e => handleCountryChange(e.target.value)}
              className="payment-form__select"
              disabled={countries.length === 0}
            >
              <option value="" disabled>
                {countries.length === 0 ? 'Loading countries…' : 'Select your country'}
              </option>
              {countries.map(c => (
                <option key={c.country} value={c.country}>
                  {c.displayName?.en || c.displayName?.fr || c.country}
                </option>
              ))}
            </select>
          </div>
          <div className="payment-form__row">
            <label>Currency</label>
            <select
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              className="payment-form__select"
              disabled={currencyOptions.length === 0}
            >
              <option value="" disabled>
                {selectedCountry ? 'Select a currency' : 'Select a country first'}
              </option>
              {currencyOptions.map(c => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="payment-form__row">
            <label>Phone number (E.164 format)</label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+260971234567"
              required
              className="payment-form__input"
            />
          </div>
          {error && <p className="payment-form__error">{error}</p>}
          <button type="submit" disabled={loading || !currency} className="payment-form__submit">
            {loading ? 'Initiating…' : `Pay $${tierInfo?.price.toFixed(2)} via Mobile Money`}
          </button>
          <p className="payment-form__note">
            No account needed. You will receive a USSD prompt to approve.
          </p>
        </form>
      )}

      {polling && (
        <div className="mobile-polling">
          <div className="mobile-polling__spinner" />
          <p className="mobile-polling__msg">{statusMsg}</p>
          <p className="mobile-polling__sub">
            Do not close this tab until you see your key.
          </p>
        </div>
      )}
    </div>
  );
}

function PurchaseFlow({ tier, onBack, onSuccess }) {
  const [method, setMethod] = useState('stripe');
  const tierInfo = TIERS.find(t => t.id === tier);

  return (
    <div className="purchase-flow">
      <button className="purchase-flow__back" onClick={onBack}>← Back to tiers</button>
      <div className="purchase-flow__header">
        <h2>{tierInfo?.name} — ${tierInfo?.price.toFixed(2)}/month</h2>
        <p>{tierInfo?.rateLimit} · 100% anonymous</p>
      </div>

      <div className="method-tabs">
        <button
          className={`method-tab ${method === 'stripe' ? 'active' : ''}`}
          onClick={() => setMethod('stripe')}
        >
          Card Payment
        </button>
        <button
          className={`method-tab ${method === 'pawapay' ? 'active' : ''}`}
          onClick={() => setMethod('pawapay')}
        >
          Mobile Money (Africa)
        </button>
      </div>

      {method === 'stripe' && !STRIPE_KEY && (
        <p className="payment-form__error">
          Card payments are not configured. Please use Mobile Money.
        </p>
      )}

      {method === 'stripe' && STRIPE_KEY && (
        <Elements stripe={stripePromise}>
          <StripePaymentForm tier={tier} onSuccess={onSuccess} />
        </Elements>
      )}

      {method === 'pawapay' && (
        <MobileMoneyForm tier={tier} onSuccess={onSuccess} />
      )}
    </div>
  );
}

export default function ApiAccessPage() {
  const [selectedTier, setSelectedTier] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [keyTier, setKeyTier] = useState(null);

  const handleSuccess = (key, tier) => {
    setApiKey(key);
    setKeyTier(tier);
    setSelectedTier(null);
  };

  return (
    <div className="api-access">
      <header className="api-access__header">
        <h1>Proteste Data API</h1>
        <p>
          Access anonymized civic voice data — sentiments, geographic patterns, viewpoint clusters,
          and AI synthesis across 70+ global sources. No account required.
        </p>
      </header>

      {apiKey ? (
        <KeyReveal apiKey={apiKey} tier={keyTier} />
      ) : selectedTier ? (
        <PurchaseFlow
          tier={selectedTier}
          onBack={() => setSelectedTier(null)}
          onSuccess={handleSuccess}
        />
      ) : (
        <>
          <div className="tier-grid">
            {TIERS.map(tier => (
              <div key={tier.id} className="tier-card" style={{ '--tier-color': tier.color }}>
                <div className="tier-card__badge">{tier.badge}</div>
                <h3 className="tier-card__name">{tier.name}</h3>
                <div className="tier-card__price">
                  <span className="tier-card__amount">${tier.price.toFixed(2)}</span>
                  <span className="tier-card__period">/month</span>
                </div>
                <p className="tier-card__rate">{tier.rateLimit}</p>
                <ul className="tier-card__features">
                  {tier.features.map(f => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <button
                  className="tier-card__cta"
                  onClick={() => setSelectedTier(tier.id)}
                >
                  Get {tier.name} key
                </button>
              </div>
            ))}
          </div>

          <div className="api-access__free">
            <h3>Free tier — 100 req/day</h3>
            <p>
              Register a free API key via{' '}
              <code>POST /api/v1/keys/register</code> — no payment needed.
              Perfect for testing and personal projects.
            </p>
          </div>

          <div className="api-access__docs">
            <h3>How it works</h3>
            <ol>
              <li>Choose a tier and pay anonymously (card or mobile money)</li>
              <li>Your API key appears on screen — copy it immediately</li>
              <li>Pass it in the <code>X-API-Key</code> header with every request</li>
              <li>No account, no tracking, no email required</li>
            </ol>
            <div className="api-access__endpoints">
              <h4>Key endpoints</h4>
              <div className="endpoint-list">
                <code>GET /api/v1/trends</code>
                <code>GET /api/v1/sentiment?topic_id=</code>
                <code>GET /api/v1/geo/heatmap</code>
                <code>GET /api/v1/synthesis/{'{topic_id}'}</code>
                <code>GET /api/v1/topics/search?q=</code>
                <code>POST /api/v1/webhooks/subscribe</code>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
